/**
 * Verify — default, loading, error, expired and verified.
 *
 * Visual Reference: `design/screens/auth-verify.html`. Its notes are the spec:
 *
 *   · error — "the send failed, said so in plain words, and left the button
 *     live so the fix is one click. No code, no apology."
 *   · verified — "It confirms the address rather than just saying 'success',
 *     then points at the one thing to do next."
 *   · expired — "24 hours stated, and the way out is a primary button rather
 *     than a support address."
 *
 * PRD §3.14 requires verification before VOICE enablement, not before a
 * session — which is why this screen is somewhere a signed-in person lands, not
 * a gate in front of the product.
 *
 * The 24 hours is rendered from `LINK_LIFETIME_SECONDS.verify`, the same
 * constant the email template states, so the screen and the mail agree.
 *
 * Story 3.4.
 */
import { useEffect, useRef, useState } from 'react'
import { LINK_LIFETIME_SECONDS, RESEND_COOLDOWN_SECONDS } from '@leadgible/schema'
import { AuthCard, AuthFootnote, AuthLink, CardHeading, CardMark } from './AuthPanel'
import { Button } from '../ui/Button'
import { Callout } from '../ui/Callout'
import { useCountdown } from '../ui/useCountdown'
import { apiPost, ApiFailure } from '../lib/api'

const VERIFY_HOURS = LINK_LIFETIME_SECONDS.verify / 3600

export function VerifyEmail() {
  const params = new URLSearchParams(window.location.search)
  const address = params.get('email') ?? 'your address'
  /**
   * The ERROR is read before the status, and the order is the whole point.
   *
   * The API points the emailed link at `/verify?status=verified` (see
   * `services/auth`), because Better Auth's success redirect appends nothing of
   * its own. Its FAILURE redirect appends to that same URL — `&error=CODE`,
   * since ours already carries a `?` — so a dead link arrives as
   * `?status=verified&error=TOKEN_EXPIRED`. Reading `status` first would tell
   * somebody holding an expired link that they are confirmed, which is the one
   * wrong answer this screen can give: they would leave believing a thing that
   * is not true, and nothing would ever correct them.
   *
   * `TOKEN_EXPIRED` is the 24-hour case the design draws. Every other code —
   * `INVALID_TOKEN`, `USER_NOT_FOUND`, `EMAIL_ALREADY_VERIFIED` — falls to the
   * default state, which offers a resend and is the right move for all three.
   * fixes: 2026-09-07, with the GET 404 that hid it.
   */
  const [state, setState] = useState<'default' | 'expired' | 'verified'>(() => {
    const error = params.get('error')
    if (error) return error === 'TOKEN_EXPIRED' ? 'expired' : 'default'
    if (params.get('status') === 'verified') return 'verified'
    if (params.get('status') === 'expired') return 'expired'
    return 'default'
  })
  const [sending, setSending] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const { remaining, start } = useCountdown()

  /**
   * The cooldown runs on arrival ONLY when a mail has just gone out — the flow
   * that sends one lands here with `?sent=1`.
   *
   * `auth-verify.html`'s default state shows "Resend the email · 30s" because it
   * depicts the moment after a send, and the first build read that as the
   * screen's invariant: a cooldown on every mount. That would block a legitimate
   * resend for somebody who comes back ten minutes later and finds nothing in
   * their inbox — which is precisely the person this screen exists for.
   */
  const justSent = params.get('sent') === '1'
  useEffect(() => {
    // Once, on arrival. The countdown owns its own ticking after that.
    if (justSent) start(RESEND_COOLDOWN_SECONDS)
  }, [justSent, start])

  async function resend() {
    setSending(true)
    setFailure(null)
    try {
      // The API rewrites `callbackURL` to an absolute dashboard URL before the
      // mail goes out (`services/auth`), so this value is a statement of intent
      // rather than the one that ships. Where an emailed link lands is the
      // API's to decide: it is the only party that knows `APP_ORIGIN`.
      await apiPost('/v1/auth/send-verification-email', { email: address, callbackURL: '/verify' })
      start(RESEND_COOLDOWN_SECONDS)
      setState('default')
    } catch (error) {
      setFailure(error instanceof ApiFailure ? error : null)
    } finally {
      setSending(false)
    }
  }

  /**
   * A post-sign-up arrival SENDS one, rather than claiming one was sent.
   *
   * `?send=1` is set by `SignUp.tsx` and by nothing else. It is a POSITIVE
   * marker and not the absence of `sent`/`status`/`error`, because this screen
   * is also somewhere people arrive from a bookmark, from an emailed link's
   * redirect, and from "Use a different address" — inferring intent from what a
   * URL lacks would send mail on all three.
   *
   * The ref is what makes it once. `resend` is rebuilt every render so it
   * cannot be a dependency, and React's development StrictMode mounts twice —
   * without the guard that is two verification emails for one sign-up, and the
   * second would reset the cooldown the first started.
   *
   * `resend()` already owns every state the design draws for this moment:
   * `sending` renders the mockup's loading state ("the resend button becomes
   * 'Sending' in place"), success starts the cooldown that `auth-verify.html`
   * draws pre-cooled, and a failure leaves the button live under the error
   * Callout. Until now the loading state was unreachable except by clicking.
   *
   * fixes: sign-up sent nothing while the screen said "We sent a link to
   * {address}" and disabled Resend for 30 seconds. 2026-09-08
   */
  const sendOnArrival = params.get('send') === '1' && params.get('email') !== null
  const asked = useRef(false)
  useEffect(() => {
    if (!sendOnArrival || asked.current) return
    asked.current = true
    void resend()
    // `resend` is deliberately not a dependency — it is re-created every
    // render, so listing it would re-run this on each one. `asked` is the guard
    // that makes the send once, and it does not depend on the dep array.
  }, [sendOnArrival])

  if (state === 'verified') {
    return (
      <AuthCard>
        <CardHeading title="You're all set">
          {address} is confirmed. Your funnels can send email from here on.
        </CardHeading>
        <Button full onClick={() => window.location.assign('/')}>
          Go to your dashboard
        </Button>
      </AuthCard>
    )
  }

  if (state === 'expired') {
    return (
      <AuthCard>
        <Callout>That link has expired</Callout>
        <CardHeading title="That link has expired">
          Confirmation links last {VERIFY_HOURS} hours. Send yourself a fresh one and it will work
          straight away.
        </CardHeading>
        <div className="flex flex-col gap-mdx-20">
          <Button full loading={sending ? 'Sending' : undefined} onClick={() => void resend()}>
            Send a new one
          </Button>
          <AuthFootnote>
            <AuthLink href="/signup">Use a different address</AuthLink>
          </AuthFootnote>
        </div>
      </AuthCard>
    )
  }

  return (
    <AuthCard>
      <CardMark>
        <svg width="24" height="18" viewBox="0 0 24 18" fill="none" aria-hidden="true">
          <rect x="1" y="1" width="22" height="16" rx="2.5" stroke="currentColor" strokeWidth="2" />
          <path
            d="M2.5 3.5 12 10.5 21.5 3.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </CardMark>
      <CardHeading title="Confirm your email address">
        We sent a link to {address}. Click it and you're in.
      </CardHeading>
      <div className="flex flex-col gap-mdx-20">
        {/* The button stays LIVE on failure — the fix is one click, and a
            disabled button would make a transient send failure look permanent. */}
        {failure ? <Callout>{failure.message}</Callout> : null}
        <Button
          variant="secondary"
          disabled={remaining > 0}
          loading={sending ? 'Sending' : undefined}
          onClick={() => void resend()}
        >
          {remaining > 0 ? `Resend the email · ${remaining}s` : 'Resend the email'}
        </Button>
        <AuthFootnote>
          <AuthLink href="/signup">Use a different address</AuthLink>
        </AuthFootnote>
        <AuthFootnote>Not seeing it? Check your spam folder.</AuthFootnote>
      </div>
    </AuthCard>
  )
}
